import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import employeeService from "../../services/employeeService";
import { getErrorMessage } from "../../utils/errorHandler";
import FormCard from "../../components/ui/FormCard";
import FormSection from "../../components/ui/FormSection";
import FormActions from "../../components/ui/FormActions";
import Breadcrumb from "../../components/ui/Breadcrumb";
import "../../styles/FormCard.css";

const EmployeeNew = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [managers, setManagers] = useState([]);
  const [hrResponsibles, setHRResponsibles] = useState([]);
  const [loadingManagers, setLoadingManagers] = useState(true);
  const [loadingHR, setLoadingHR] = useState(true);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone: "",
    hire_date: "",
    department: "",
    job_title: "",
    role: "EMPLOYEE",
    manager_id: "",
    manager_n_id: "",
    hr_responsible_id: "",
    is_active: true,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadManagers();
    loadHRResponsibles();
  }, []);

  const loadManagers = async () => {
    try {
      setLoadingManagers(true);
      const data = await employeeService.getManagers();
      setManagers(data);
    } catch (err) {
      console.error("Error loading managers:", err);
    } finally {
      setLoadingManagers(false);
    }
  };

  const loadHRResponsibles = async () => {
    try {
      setLoadingHR(true);
      const data = await employeeService.getHRResponsibles();
      setHRResponsibles(data);
    } catch (err) {
      console.error("Error loading HR responsibles:", err);
    } finally {
      setLoadingHR(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.first_name.trim()) newErrors.first_name = "Le prénom est requis";
    if (!formData.last_name.trim()) newErrors.last_name = "Le nom est requis";
    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }
    if (!formData.password.trim()) newErrors.password = "Le mot de passe est requis";
    if (!formData.role) newErrors.role = "Le rôle est requis";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setSubmitting(true);
      const payload = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        hire_date: formData.hire_date,
        department: formData.department,
        job_title: formData.job_title,
        role: formData.role,
        manager_id: formData.manager_id ? parseInt(formData.manager_id) : null,
        manager_n_id: formData.manager_n_id ? parseInt(formData.manager_n_id) : null,
        hr_responsible_id: formData.hr_responsible_id ? parseInt(formData.hr_responsible_id) : null,
        is_active: formData.is_active,
      };
      await employeeService.create(payload);
      toast.success("Collaborateur ajouté avec succès");
      navigate("/rh/employees");
    } catch (err) {
      console.error("Error creating employee:", err);
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/rh/employees");
  };

  const breadcrumbItems = [
    { label: "RH", to: "/rh/dashboard" },
    { label: "Collaborateurs", to: "/rh/employees" },
    { label: "Ajouter un collaborateur" }
  ];

  const roleOptions = [
    { value: "EMPLOYEE", label: "Employé" },
    { value: "MANAGER", label: "Manager" },
    { value: "RH", label: "Responsable RH" }
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Breadcrumb items={breadcrumbItems} />
      <FormCard 
        title="Ajouter un collaborateur" 
        subtitle="Crée aussi le compte utilisateur dans la table `users`."
        showBackButton 
        onBack={handleCancel}
      >
        <form onSubmit={handleSubmit}>
          <FormSection>
            <div className="form-section-content">
              <div className="form-group">
                <label>Prénom *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className={errors.first_name ? "error" : ""}
                />
                {errors.first_name && <span className="error">{errors.first_name}</span>}
              </div>
              <div className="form-group">
                <label>Nom *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className={errors.last_name ? "error" : ""}
                />
                {errors.last_name && <span className="error">{errors.last_name}</span>}
              </div>
              <div className="form-group">
                <label>Email (login) *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? "error" : ""}
                />
                {errors.email && <span className="error">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label>Mot de passe *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={errors.password ? "error" : ""}
                />
                {errors.password && <span className="error">{errors.password}</span>}
              </div>
              <div className="form-group">
                <label>Téléphone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Date d'embauche</label>
                <input
                  type="date"
                  name="hire_date"
                  value={formData.hire_date}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Département</label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Poste</label>
                <input
                  type="text"
                  name="job_title"
                  value={formData.job_title}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Rôle *</label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className={errors.role ? "error" : ""}
                >
                  {roleOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {errors.role && <span className="error">{errors.role}</span>}
              </div>
              <div className="form-group">
                <label>Manager</label>
                <select
                  name="manager_id"
                  value={formData.manager_id}
                  onChange={handleChange}
                  disabled={loadingManagers}
                >
                  <option value="">- Aucun -</option>
                  {managers.map(manager => (
                    <option key={manager.id} value={manager.id}>
                      {manager.full_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Manager N</label>
                <select
                  name="manager_n_id"
                  value={formData.manager_n_id}
                  onChange={handleChange}
                  disabled={loadingManagers}
                >
                  <option value="">- Aucun -</option>
                  {managers.map(manager => (
                    <option key={manager.id} value={manager.id}>
                      {manager.full_name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Responsable RH</label>
                <select
                  name="hr_responsible_id"
                  value={formData.hr_responsible_id}
                  onChange={handleChange}
                  disabled={loadingHR}
                >
                  <option value="">- Aucun -</option>
                  {hrResponsibles.map(hr => (
                    <option key={hr.id} value={hr.id}>
                      {hr.full_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </FormSection>

          <div style={{ marginTop: "24px", marginBottom: "24px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                style={{ width: "16px", height: "16px" }}
              />
              <span>Actif</span>
            </label>
            <p style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px", marginLeft: "24px" }}>
              Le compte peut se connecter
            </p>
          </div>

          <FormActions
            onCancel={handleCancel}
            onSave={handleSubmit}
            saveText="+ Créer"
            isLoading={submitting}
          />
        </form>
      </FormCard>
    </div>
  );
};

export default EmployeeNew;

